import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import { Capacitor } from "@capacitor/core";
import { getToken } from "../../../utils/tokenService";
import { savePdfBlob } from "../../../utils/nativePdfSave";
import "./ExperienceLetter.css";
import "../OfferLetter/style.css";

const DEFAULT_SCALE = 1;
const MM_TO_PX = 3.78;
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

const formatDisplayDate = (value) => {
  if (!value) return "N/A";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleDateString("en-GB");
};

const calculateScale = () => {
  if (typeof window === "undefined") return DEFAULT_SCALE;
  const a4WidthPx = A4_WIDTH_MM * MM_TO_PX;
  // 48px toolbar padding + 40px page-wrapper padding + 16px container
  const availableWidth = Math.max(window.innerWidth - 104, 240);
  return Math.min(DEFAULT_SCALE, availableWidth / a4WidthPx);
};

const resolveCompanyAssetUrl = (rawPath) => {
  const value = String(rawPath || "").trim();
  if (!value) return "";
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("/")) {
    return `${process.env.REACT_APP_BACKEND_URL}${value}`;
  }
  return `${process.env.REACT_APP_BACKEND_URL}/uploads/${value}`;
};

const ExperienceLetter = () => {
  const [user, setUser] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const letterRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };

        const dashRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/user-dashboard`,
          { headers }
        );
        setUser(dashRes.data?.currentUser || null);

        const lettersRes = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/user/offer-letters`,
          { headers }
        );
        const letters = lettersRes.data ?? [];
        const target =
          letters.find((l) => l.status === "accepted") || letters[0];

        if (target?._id) {
          const detailRes = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/offer-letter/user/offer-letter/${target._id}`,
            { headers }
          );
          const { letterContent, assets: a } = detailRes.data ?? {};
          if (letterContent?.companyInfo) setCompanyInfo(letterContent.companyInfo);
          if (a) setAssets(a);
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Unable to load experience letter."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const letterData = useMemo(() => {
    const hired = user?.hired || {};
    const designation = hired?.hiring_id?.job_category || "Employee";
    const startDate = hired?.date_of_joining || user?.date_of_joining;
    const endDate = user?.exitDate || user?.date_of_exiting || new Date();
    const today = new Date();
    const addressParts = [
      user?.house,
      user?.street,
      user?.landmark,
      user?.loc,
      user?.po,
      user?.subdist,
      user?.dist,
      user?.state,
      user?.zip,
    ].filter(Boolean);

    return {
      employeeName: user?.full_Name || "Employee Name",
      designation,
      employeeAddressLines:
        addressParts.length > 0
          ? [
              addressParts.slice(0, Math.ceil(addressParts.length / 2)).join(", "),
              addressParts.slice(Math.ceil(addressParts.length / 2)).join(", "),
            ].filter(Boolean)
          : ["Employee Address"],
      issueDate: formatDisplayDate(today),
      startDate: formatDisplayDate(startDate),
      endDate: formatDisplayDate(endDate),
      performance: user?.status === "Active" ? "good" : "satisfactory",
    };
  }, [user]);

  const companyAddressLines = useMemo(() => {
    const parts = String(companyInfo?.address || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length === 0) return [];

    return [
      parts.slice(0, Math.ceil(parts.length / 2)).join(", "),
      parts.slice(Math.ceil(parts.length / 2)).join(", "),
    ].filter(Boolean);
  }, [companyInfo]);

  useEffect(() => {
    const updateScale = () => {
      setScale(calculateScale());
    };

    updateScale();
    window.addEventListener("resize", updateScale);

    return () => {
      window.removeEventListener("resize", updateScale);
    };
  }, []);

  const downloadPDF = async () => {
    try {
      // Generate the letter directly instead of taking an HTML screenshot.
      // html2canvas can produce a blank canvas in Android WebViews.
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const filename = "ExperienceLetter.pdf";
      const margin = 20;
      const pageWidth = 210;
      const pageHeight = 297;
      const contentWidth = pageWidth - margin * 2;
      let cursorY = 24;

      const addText = (text, options = {}) => {
        const { align = "left", fontSize = 11, style = "normal", after = 4 } = options;
        const lines = pdf.splitTextToSize(String(text || ""), contentWidth);
        // jsPDF's font size is in points while cursorY is in millimetres.
        // Use the same 1.35 line-height for rendered text and cursor movement
        // so wrapped lines never overlap.
        const lineHeightFactor = 1.35;
        const lineHeight = (fontSize * lineHeightFactor) / pdf.internal.scaleFactor;

        if (cursorY + lines.length * lineHeight > pageHeight - margin) {
          pdf.addPage();
          cursorY = margin;
        }

        pdf.setFont("helvetica", style);
        pdf.setFontSize(fontSize);
        pdf.text(lines, align === "right" ? pageWidth - margin : margin, cursorY, {
          align,
          lineHeightFactor,
        });
        cursorY += lines.length * lineHeight + after;
      };

      pdf.setTextColor(21, 101, 192);
      addText(companyInfo?.name || "COMPANY NAME", { align: "right", fontSize: 15, style: "bold", after: 1 });
      pdf.setTextColor(0, 0, 0);
      if (companyInfo?.address) addText(companyInfo.address, { align: "right", fontSize: 9, after: 1 });
      const contact = [companyInfo?.email, companyInfo?.phone].filter(Boolean).join(" | ");
      if (contact) addText(contact, { align: "right", fontSize: 9, after: 5 });
      pdf.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 13;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.text("EXPERIENCE LETTER", pageWidth / 2, cursorY, { align: "center" });
      cursorY += 16;

      addText(`Date: ${letterData.issueDate}`, { align: "right", style: "bold", after: 8 });
      addText("To,", { style: "bold", after: 2 });
      addText(letterData.employeeName, { after: 1 });
      addText(`Designation - ${letterData.designation}`, { after: 1 });
      letterData.employeeAddressLines.forEach((line) => addText(line, { after: 1 }));
      cursorY += 4;
      addText(`This is to certify that Mr./Ms. ${letterData.employeeName} was employed with ${companyInfo?.name || "the company"} as a ${letterData.designation} from ${letterData.startDate} to ${letterData.endDate}.`, { after: 5 });
      addText(`They have demonstrated strong professional skills, dedication, and a positive attitude towards their work. Their performance and conduct were found to be ${letterData.performance} during their employment.`, { after: 5 });
      addText("We appreciate their contributions to the organization and wish them all the best in their future endeavors.", { after: 5 });
      addText("If you require any further information, please feel free to contact us.", { after: 14 });
      addText("Sincerely,", { after: 14 });
      addText(`FOR ${companyInfo?.name || "THE COMPANY"}`, { style: "bold", after: 18 });
      addText("Authorised Signatory", { style: "bold" });

      if (Capacitor.isNativePlatform()) {
        await savePdfBlob(pdf.output("blob"), filename);
      } else {
        pdf.save(filename);
      }
    } catch (error) {
      console.error("[ExperienceLetter] PDF generation failed:", error);
      toast.error("Unable to generate PDF. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="experience-letter-page">
        <div className="experience-letter-shell">
          <p className="experience-letter-status">Loading experience letter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="experience-letter-page">
      <div className="experience-letter-toolbar">
        <h2>Experience Letter</h2>
        <button
          type="button"
          className="btn btn-primary"
          onClick={downloadPDF}
        >
          Download PDF
        </button>
      </div>

      <div className="offer-letter-preview container py-4">
        <div className="page-wrapper">
          {/* Wrapper sized to scaled A4 dimensions so centering works on mobile */}
          <div
            style={{
              width: `${A4_WIDTH_MM * MM_TO_PX * scale}px`,
              height: `${A4_HEIGHT_MM * MM_TO_PX * scale}px`,
              position: "relative",
              margin: "0 auto",
              flexShrink: 0,
            }}
          >
          <div
            ref={letterRef}
            className="a4-page"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              width: "210mm",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          >
            <div className="header d-flex justify-content-between align-items-center" style={{ flexDirection: "row", background: "transparent" }}>
                  {assets?.logo && (
                    <div
                      className="logo-container-user"
                      style={{
                        width: "100px",
                        marginRight: "20px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        background: "transparent",
                      }}
                    >
                      <img
                        crossOrigin="anonymous"
                        src={resolveCompanyAssetUrl(assets.logo)}
                        alt="Company Logo"
                        className="logo"
                        style={{ width: "100%", background: "transparent" }}
                      />
                    </div>
                  )}

                  <div
                    className={`company-details ${!assets?.logo ? "w-100" : ""}`}
                  >
                    <h5 className="company-name text-end" style={{ textTransform: "uppercase", color: "#1565c0" }}>
                      {companyInfo?.name}
                    </h5>
                    <div
                      className="text-end"
                      style={{ marginBottom: "0rem", paddingBottom: "0.2rem" }}
                    >
                      {(() => {
                        const addr = companyInfo?.address || "";
                        const mid = Math.floor(addr.length / 2);
                        const splitAt = addr.indexOf(",", mid);
                        if (splitAt === -1) return addr;
                        return (
                          <>
                            {addr.slice(0, splitAt + 1)}
                            <br />
                            {addr.slice(splitAt + 1).trim()}
                          </>
                        );
                      })()}
                    </div>
                    <div
                      className="contact-info text-end"
                      style={{ marginTop: "0rem", paddingTop: "0.2rem" }}
                    >
                      Email: {companyInfo?.email}
                      {companyInfo?.phone && (
                        <>
                          <span className="mx-1">,</span>
                          PH NO-{companyInfo.phone}
                        </>
                      )}
                    </div>
                  </div>
            </div>

            <hr className="divider" />

            <div className="letter-title text-center my-4">
              <h2 className="text-decoration-underline">Experience Letter</h2>
            </div>

            <div className="date-info" style={{ textAlign: "right", marginBottom: "20px" }}>
              <strong>Date:</strong> {letterData.issueDate}
            </div>

            <div className="letter-body">
              <p><strong>To,</strong></p>
              <p>{letterData.employeeName}</p>
              <p>Designation - {letterData.designation}</p>
              {letterData.employeeAddressLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
              <p>
                This is to certify that Mr./Ms. {letterData.employeeName} was
                employed with {companyInfo?.name} as a{" "}
                {letterData.designation} from {letterData.startDate} to{" "}
                {letterData.endDate}.
              </p>
              <p>
                They have demonstrated strong professional skills, dedication,
                and a positive attitude towards their work. Their performance and
                conduct were found to be {letterData.performance} during their
                employment.
              </p>
              <p>
                We appreciate their contributions to the organization and wish
                them all the best in their future endeavors.
              </p>
              <p>
                If you require any further information, please feel free to
                contact us.
              </p>
            </div>

            <div className="signature-section mt-5">
              <p>Sincerely,</p>
              <p className="mt-4">FOR {companyInfo?.name}</p>
              {assets?.signature && (
                <div style={{ width: 150, height: 80, position: "relative" }}>
                  <img
                    crossOrigin="anonymous"
                    src={resolveCompanyAssetUrl(assets.signature)}
                    alt="Authorised Signature"
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              )}
              <p className="mt-4">Authorised Signatory</p>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperienceLetter;
